import { useEffect, useState, useCallback } from "react";
import { Navigate, Link } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { api } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { formatPoints } from "@/lib/format";
import { formatPesos } from "@/lib/currency";
import { Crown, TrendingUp, Trophy, Zap, Plus } from "lucide-react";
import CreateMarketForm from "@/components/CreateMarketForm";

const TIER_INFO = {
    bronze: { label: "Bronze", pct: 5, color: "text-p2w-amber", bg: "bg-p2w-amber/15 border-p2w-amber/40" },
    silver: { label: "Silver", pct: 6, color: "text-white", bg: "bg-white/15 border-white/40" },
    gold: { label: "Gold", pct: 7, color: "text-p2w-green", bg: "bg-p2w-green/15 border-p2w-green/40" },
};

export default function CreatorDashboard() {
    const { user } = useAuth();
    const [data, setData] = useState(null);
    const [categories, setCategories] = useState([]);
    const [creating, setCreating] = useState(false);

    const reload = useCallback(() => {
        api.get("/creators/me/dashboard").then((r) => setData(r.data));
    }, []);

    useEffect(() => {
        if (user && user !== false && user.is_creator) {
            reload();
            api.get("/categories").then((r) => setCategories(r.data || [])).catch(() => {});
        }
    }, [user, reload]);
    if (user === false) return <Navigate to="/login" replace />;
    if (!user) return null;
    if (!user.is_creator) return <Navigate to="/creadores" replace />;
    if (!data) return <div className="max-w-6xl mx-auto px-6 py-12"><div className="h-64 bg-p2w-surface border border-p2w-line rounded-2xl animate-pulse" /></div>;

    const tier = TIER_INFO[data.tier] || TIER_INFO.bronze;
    const isSuspended = data.creator_status === "suspended";

    const onMarketCreated = () => {
        setCreating(false);
        reload();
    };

    return (
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-12" data-testid="creator-dashboard">
            {/* Single dialog instance reused by both trigger buttons (header + empty state). */}
            <Dialog open={creating} onOpenChange={setCreating}>
                <DialogContent className="bg-p2w-surface border-p2w-line text-white max-w-xl max-h-[85vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle className="font-sora">Crear mercado</DialogTitle>
                        <DialogDescription className="text-xs text-p2w-gray">
                            Como @{data.creator_handle} (tier {tier.label}) ganas {tier.pct}% del pozo cuando se resuelva.
                        </DialogDescription>
                    </DialogHeader>
                    <CreateMarketForm
                        categories={categories}
                        forCreator={true}
                        onCreated={onMarketCreated}
                    />
                </DialogContent>
            </Dialog>

            <div className="flex items-end justify-between flex-wrap gap-4">
                <div>
                    <div className="flex items-center gap-2 text-p2w-purple text-xs uppercase tracking-[0.3em] font-bold">
                        <Crown className="w-4 h-4" /> Creator Dashboard
                    </div>
                    <h1 className="font-sora font-extrabold text-4xl tracking-tighter mt-1">@{data.creator_handle}</h1>
                </div>
                <div className="flex items-center gap-3 flex-wrap">
                    <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-xl border ${tier.bg}`}>
                        <Trophy className={`w-4 h-4 ${tier.color}`} />
                        <span className={`font-sora font-bold ${tier.color}`}>{tier.label}</span>
                        <span className="text-xs text-p2w-gray">· {tier.pct}% comisión</span>
                    </div>
                    <Button
                        onClick={() => setCreating(true)}
                        disabled={isSuspended}
                        data-testid="creator-create-market-btn"
                        title={isSuspended ? "Tu cuenta de creador está suspendida" : "Crear un nuevo mercado bajo tu handle"}
                        className="bg-p2w-green text-p2w-green-fg hover:bg-p2w-green/90 font-semibold disabled:opacity-50"
                    >
                        <Plus className="w-4 h-4 mr-1.5" /> Crear nuevo mercado
                    </Button>
                </div>
            </div>

            {isSuspended && (
                <div className="mt-6 rounded-2xl bg-p2w-red/10 border border-p2w-red/40 p-5 text-p2w-red text-sm">
                    Tu cuenta de creador está <strong>suspendida</strong>. No puedes crear mercados hasta que un admin la reactive.
                </div>
            )}

            <div className="mt-8 grid grid-cols-2 lg:grid-cols-4 gap-4">
                <Stat icon={Zap} label="Mercados activos" value={data.markets_active} accent="text-p2w-magenta" />
                <Stat icon={TrendingUp} label="Volumen total" value={formatPesos(data.total_volume)} accent="text-white" />
                <Stat icon={Trophy} label="Comisión acumulada" value={formatPesos(data.total_commission)} accent="text-p2w-green" />
                <Stat label="Mercados resueltos" value={data.markets_resolved} accent="text-p2w-gray" />
            </div>

            <h2 className="font-sora font-bold text-xl mt-10 mb-3">Mis ingresos · ledger</h2>
            <div className="rounded-2xl border border-p2w-line bg-p2w-surface overflow-hidden">
                {data.earnings.length === 0 ? (
                    <div className="p-10 text-center text-p2w-gray">Aún no tienes comisiones. Crea tu primer mercado.</div>
                ) : (
                    <table className="w-full text-sm">
                        <thead className="bg-p2w-elevated border-b border-p2w-line text-p2w-gray text-xs uppercase tracking-wider">
                            <tr><th className="text-left px-4 py-3">Mercado</th><th className="text-right px-4 py-3">Comisión</th><th className="text-right px-4 py-3">Fecha</th></tr>
                        </thead>
                        <tbody>
                            {data.earnings.map((e) => (
                                <tr key={e.id} className="border-t border-p2w-line">
                                    <td className="px-4 py-3"><Link to={`/mercado/${e.ref_market_id}`} className="text-white hover:text-p2w-green">{e.ref_market_id?.slice(0, 8)}…</Link></td>
                                    <td className="px-4 py-3 text-right font-sora font-bold text-p2w-green number-tabular">+{formatPoints(e.amount)} pts</td>
                                    <td className="px-4 py-3 text-right text-xs text-p2w-gray">{new Date(e.created_at).toLocaleString()}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>

            <h2 className="font-sora font-bold text-xl mt-10 mb-3">Mis mercados</h2>
            <div className="rounded-2xl border border-p2w-line bg-p2w-surface overflow-hidden">
                {data.markets.length === 0 ? (
                    <div className="p-10 text-center space-y-4">
                        <div className="text-p2w-gray">No tienes mercados aún. ¡Crea tu primero!</div>
                        <Button
                            onClick={() => setCreating(true)}
                            disabled={isSuspended}
                            data-testid="creator-empty-create-market-btn"
                            className="bg-p2w-green text-p2w-green-fg hover:bg-p2w-green/90 font-semibold disabled:opacity-50"
                        >
                            <Plus className="w-4 h-4 mr-1.5" /> Crear nuevo mercado
                        </Button>
                    </div>
                ) : (
                    <table className="w-full text-sm">
                        <thead className="bg-p2w-elevated border-b border-p2w-line text-p2w-gray text-xs uppercase tracking-wider">
                            <tr><th className="text-left px-4 py-3">Título</th><th className="text-left px-4 py-3">Estado</th><th className="text-right px-4 py-3">Pozo</th></tr>
                        </thead>
                        <tbody>
                            {data.markets.map((m) => (
                                <tr key={m.id} className="border-t border-p2w-line">
                                    <td className="px-4 py-3"><Link to={`/mercado/${m.id}`} className="text-white hover:text-p2w-green">{m.title}</Link></td>
                                    <td className="px-4 py-3"><Badge className="bg-p2w-elevated border border-p2w-line text-[10px] uppercase">{m.status}</Badge></td>
                                    <td className="px-4 py-3 text-right font-sora font-bold text-p2w-green number-tabular">{formatPesos(m.total_pool)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>
        </div>
    );
}

function Stat({ icon: Icon, label, value, accent }) {
    return (
        <div className="rounded-2xl border border-p2w-line bg-p2w-surface p-5">
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-p2w-gray">
                {Icon && <Icon className="w-3 h-3" />}{label}
            </div>
            <div className={`mt-2 font-sora font-extrabold text-3xl number-tabular ${accent || "text-white"}`}>{value}</div>
        </div>
    );
}
